import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { syncTokenStorage } from '../utils/auth';

// API base URL for authentication
const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.quickcoverletter.com';

// Function to notify the Chrome extension when signed in
const notifyChromeExtension = async (token: string): Promise<boolean> => {
  if (!token) {
    console.error('[AUTH-CALLBACK] No token provided for extension notification');
    return false;
  }

  // Ensure chrome object and runtime are available
  if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
    console.warn('[AUTH-CALLBACK] Chrome runtime not available for sending message.');
    // Fallback to localStorage might still be useful here if direct messaging isn't possible
    try {
        localStorage.setItem('quickCoverLetter_pendingToken', token);
        localStorage.setItem('quickCoverLetter_pendingTokenTimestamp', Date.now().toString());
        setTimeout(() => {
            localStorage.removeItem('quickCoverLetter_pendingToken');
            localStorage.removeItem('quickCoverLetter_pendingTokenTimestamp');
        }, 120000); // Clear after 2 mins
        console.log('[AUTH-CALLBACK] Used localStorage fallback for token.');
        return true; // Indicate success for fallback
    } catch (storageError) {
        console.error('[AUTH-CALLBACK] Failed to store token in localStorage:', storageError);
        return false;
    }
  }

  // Get the production extension ID from environment variables or use a default
  const productionExtensionId = process.env.REACT_APP_CHROME_EXTENSION_ID || 'jpmbdiimnlbllmckhoobfalnbollngpb'; // Use your actual production ID
  const developmentExtensionId = 'cfcblphdpoofpoagliebplmcblmkpphc'; // Your development ID

  // Determine which ID to use based on the environment (simple check)
  const targetExtensionId = window.location.hostname === 'localhost' ? developmentExtensionId : productionExtensionId;

  console.log(`[AUTH-CALLBACK] Attempting to send message to extension ID: ${targetExtensionId}`);

  try {
    const response = await new Promise<any>((resolve, reject) => {
      // @ts-ignore - Chrome types might not be fully up-to-date or available
      chrome.runtime.sendMessage(
        targetExtensionId,
        { type: 'AUTH_STATE_CHANGE', token },
        (response) => {
          if (chrome.runtime.lastError) {
            // Log specific error details
            console.error(`[AUTH-CALLBACK] Error sending message to ${targetExtensionId}:`, chrome.runtime.lastError.message);
            // Resolve with failure instead of rejecting to handle gracefully
            resolve({ success: false, error: chrome.runtime.lastError.message });
            return;
          }
          console.log(`[AUTH-CALLBACK] Response from extension ${targetExtensionId}:`, response);
          resolve(response || { success: true }); // Assume success if no response object but no error
        }
      );

      // Add a timeout in case the extension doesn't respond
      setTimeout(() => {
        resolve({ success: false, error: 'Timeout waiting for extension response' });
      }, 2000); // 2 second timeout
    });

    if (response && response.success) {
      console.log(`[AUTH-CALLBACK] Successfully notified extension ${targetExtensionId}`);
      return true;
    } else {
      console.warn(`[AUTH-CALLBACK] Failed to notify extension ${targetExtensionId}. Response:`, response);
      // Attempt localStorage fallback if messaging failed
      try {
          localStorage.setItem('quickCoverLetter_pendingToken', token);
          localStorage.setItem('quickCoverLetter_pendingTokenTimestamp', Date.now().toString());
          setTimeout(() => {
              localStorage.removeItem('quickCoverLetter_pendingToken');
              localStorage.removeItem('quickCoverLetter_pendingTokenTimestamp');
          }, 120000);
          console.log('[AUTH-CALLBACK] Used localStorage fallback after messaging failure.');
          return true; // Indicate success for fallback
      } catch (storageError) {
          console.error('[AUTH-CALLBACK] Failed to store token in localStorage after messaging failure:', storageError);
          return false;
      }
    }
  } catch (error) {
    console.error(`[AUTH-CALLBACK] Exception sending message to extension ${targetExtensionId}:`, error);
    return false;
  }
};

// Function to sync subscription status with Chrome extension
const syncSubscriptionWithExtension = async (subscriptionData: any): Promise<boolean> => {
  if (!subscriptionData) {
    console.error('[AUTH-CALLBACK] No subscription data provided for extension sync');
    return false;
  }

  try {
    console.log('[AUTH-CALLBACK] Syncing subscription data with extension:', subscriptionData);
    
    // List of known extension IDs across environments
    const extensionIds = [
      'jpmbdiimnlbllmckhoobfalnbollngpb', // Production extension
      'gipbfpigdhbfmpfohplncjlmdnlfkaga', // Old extension ID
      'cfcblphdpoofpoagliebplmcblmkpphc', // Development extension
    ];
    
    // Try sending to each registered extension ID
    let successfulSync = false;
    
    for (const extensionId of extensionIds) {
      try {
        if (!chrome || !chrome.runtime) {
          console.log('[AUTH-CALLBACK] Chrome runtime not available for subscription sync');
          continue;
        }
        
        console.log(`[AUTH-CALLBACK] Sending subscription data to extension ID: ${extensionId}`);
        
        // Send subscription data to extension
        const response = await new Promise<any>((resolve, reject) => {
          try {
            // @ts-ignore - Chrome extension API might not be available
            chrome.runtime.sendMessage(
              extensionId,
              { 
                type: 'SUBSCRIPTION_STATE_CHANGE', 
                subscriptionData: {
                  status: subscriptionData.status,
                  plan: subscriptionData.plan,
                  expiresAt: subscriptionData.expires_at,
                  remainingCredits: subscriptionData.remaining_credits,
                  updatedAt: new Date().toISOString()
                }
              },
              (response) => {
                if (chrome.runtime.lastError) {
                  console.warn(`[AUTH-CALLBACK] Error syncing subscription with ${extensionId}:`, chrome.runtime.lastError);
                  resolve({ success: false, error: chrome.runtime.lastError });
                  return;
                }
                resolve(response || { success: true });
              }
            );
            
            // Add a timeout to avoid hanging if no response
            setTimeout(() => {
              resolve({ success: false, error: 'Timeout' });
            }, 1000);
          } catch (err) {
            console.error(`[AUTH-CALLBACK] Exception syncing with ${extensionId}:`, err);
            reject(err);
          }
        });
        
        console.log(`[AUTH-CALLBACK] Subscription sync response from ${extensionId}:`, response);
        
        if (response && response.success) {
          console.log(`[AUTH-CALLBACK] Successfully synced subscription with extension ${extensionId}`);
          successfulSync = true;
          break;
        }
      } catch (extensionError) {
        console.warn(`[AUTH-CALLBACK] Failed to sync subscription with extension ${extensionId}:`, extensionError);
      }
    }
    
    // Store in localStorage as fallback for extension to pick up later
    try {
      console.log('[AUTH-CALLBACK] Storing subscription data in localStorage for extension fallback');
      localStorage.setItem('quickCoverLetter_subscriptionData', JSON.stringify({
        status: subscriptionData.status,
        plan: subscriptionData.plan,
        expiresAt: subscriptionData.expires_at,
        remainingCredits: subscriptionData.remaining_credits,
        updatedAt: new Date().toISOString()
      }));
      
      // Set a timeout to clear the localStorage after 2 minutes to avoid stale data
      setTimeout(() => {
        localStorage.removeItem('quickCoverLetter_subscriptionData');
      }, 120000);
    } catch (storageError) {
      console.error('[AUTH-CALLBACK] Failed to store subscription data in localStorage:', storageError);
    }
    
    return successfulSync;
  } catch (error) {
    console.error('[AUTH-CALLBACK] Error syncing subscription with extension:', error);
    return false;
  }
};

// Function to get user profile data
const getUserProfile = async () => {
  try {
    // Fetch user profile from API
    const response = await fetch(`${apiBaseUrl}/user/profile`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access_token")}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch user profile');
    }
    
    const userData = await response.json();
    return { userData };
  } catch (error) {
    console.error("Error fetching user profile:", error);
    return { error };
  }
};

// Function to clear session data
const clearSession = () => {
  localStorage.removeItem("access_token");
  // Any other session cleanup logic would go here
};

// Function to check if session is valid
const checkIfSessionIsValid = async (): Promise<boolean> => {
  try {
    const { data } = await supabase.auth.getSession();
    return Boolean(data?.session);
  } catch (error) {
    console.error("Error checking session validity:", error);
    return false;
  }
};

/**
 * AuthCallback - Handles OAuth redirects and session establishment
 * This page is specifically designed to handle the callback after
 * OAuth authentication with Supabase, particularly for PKCE flow
 */
const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState<string>('Processing authentication...');

  useEffect(() => {
    async function checkSession() {
      try {
        // Get the code and state from URL
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        const state = urlParams.get("state");
        const isExtensionFlow = urlParams.get("extension") === "true";

        if (!code || !state) {
          console.error("No code or state found in URL parameters");
          setError("Authentication failed. Missing parameters.");
          setLoading(false);
          return;
        }

        // Set loading flag
        setLoading(true);

        const savedState = localStorage.getItem("auth_state");
        if (state !== savedState) {
          console.error("State mismatch", { state, savedState });
          setError("Authentication failed. State mismatch.");
          setLoading(false);
          return;
        }

        // Clear the saved state
        localStorage.removeItem("auth_state");

        // Exchange the code for a token
        const response = await fetch(
          `${apiBaseUrl}/auth/google/callback?code=${code}`
        );

        // Handle failure
        if (!response.ok) {
          console.error("Failed to exchange code for token", {
            status: response.status,
          });
          setError("Authentication failed. Please try again.");
          setLoading(false);
          return;
        }

        const data = await response.json();
        const accessToken = data.access_token;

        // Save token
        if (accessToken) {
          // Sync tokens
          await syncTokenStorage(accessToken);
          console.log("Tokens synced successfully");

          // Load user profile with the new token
          console.log("Loading user profile");
          const { userData, error: userError } = await getUserProfile();

          if (userError || !userData) {
            console.error("Failed to load user profile after auth", userError);
            setError("Failed to load your profile. Please try again.");
            setLoading(false);
            
            // Clear any stale auth data
            localStorage.removeItem("access_token");
            clearSession();
            return;
          }

          console.log("User profile loaded successfully", userData);
          
          // If this was triggered from the extension, notify it
          if (isExtensionFlow) {
            console.log("Extension flow detected - notifying extension");
            await notifyChromeExtension(accessToken);
            
            // Also sync subscription status with extension
            if (userData.subscription) {
              console.log("Syncing subscription status with extension");
              await syncSubscriptionWithExtension(userData.subscription);
            }
          }

          // Navigate based on user's state and origin
          if (userData.onboarded) {
            const redirect = localStorage.getItem("auth_redirect");
            localStorage.removeItem("auth_redirect");
            
            if (redirect && redirect !== "/login" && redirect !== "/signup") {
              navigate(redirect);
            } else {
              navigate("/dashboard");
            }
          } else {
            navigate("/onboarding");
          }
        } else {
          console.error("No access token returned from server");
          setError("Authentication failed. No access token received.");
          setLoading(false);
        }
      } catch (error) {
        console.error("Error during authentication", error);
        setError("An unexpected error occurred. Please try again.");
        setLoading(false);
      }
    }

    // Trigger the session check
    checkSession();

    // Set a timer to check session after 5 seconds as a fallback
    const timer = setTimeout(() => {
      checkIfSessionIsValid().then((isValid) => {
        if (isValid) {
          console.log("Session is valid from fallback timer");
          navigate("/dashboard");
        }
      });
    }, 5000);

    return () => clearTimeout(timer);
  }, [navigate]);

  // Show a loading state while processing the callback
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
      <div className="p-8 bg-white rounded-lg shadow-md text-center">
        {loading ? (
          <>
            <div className="animate-spin mb-4 mx-auto w-12 h-12 border-t-2 border-b-2 border-[#CAF84C] rounded-full"></div>
            <h2 className="text-xl font-semibold">{statusMessage}</h2>
            <p className="text-gray-600 mt-2">Please wait while we complete the authentication process.</p>
          </>
        ) : error ? (
          <>
            <div className="text-red-500 mb-4 mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-red-700">Authentication Error</h2>
            <p className="text-gray-700 mt-2">{error}</p>
            <button 
              onClick={() => navigate('/')} 
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Return to Home
            </button>
          </>
        ) : (
          <>
            <div className="text-green-500 mb-4 mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold">{statusMessage}</h2>
            <p className="text-gray-600 mt-2">You may close this window if it doesn't close automatically.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
